import dayjs from 'dayjs';
import { COMMENT_TYPE, PRIORITY_ICON_NAMES } from '../../../global/constants';
import { floatToHHMM } from '../../../helpers/common';
import { TeamActivityCommentInterface, TeamActivityInterface } from '../../../interfaces/ReportInterface';

// Minimal shape needed to resolve a member id to a display name. Kept structural so the
// workspace users array (UsersWorkspaceInterface[]) can be passed in directly.
interface TimelineUserLookup {
  id: string;
  name: string;
}

// Flat row consumed by react-csv. Keys match TimelineReportCSVHeader in global/row.
export interface TimelineExportRow {
  date: string;
  member: string;
  activityType: string;
  description: string;
  project: string;
  task: string;
}

const ACTIVITY_TYPE_LABELS: { [key: string]: string } = {
  task_created: 'Task Created',
  project_created: 'Project Created',
  newProject_member_added: 'Member Added',
  invited_user: 'User Invited',
  task_comment: 'Task Activity'
};

const findUserName = (users: TimelineUserLookup[] | undefined, id?: string | null) =>
  users?.find((user) => user.id === id)?.name || 'Unknown';

const formatDueDate = (value?: string | null | Date) => (value ? dayjs(value).format('ddd, MMM DD, YYYY') : '');

// Plain-text equivalent of renderCommentText() in timeLine.tsx. Returns null for comment
// types that have no textual representation, so the caller can skip those rows (matching the
// UI, which renders nothing for them).
const getCommentDescription = (
  comment: Partial<TeamActivityCommentInterface>,
  users: TimelineUserLookup[] | undefined
): string | null => {
  const assigneeName = findUserName(users, comment.Assignee);
  switch (comment.Type) {
    case COMMENT_TYPE.STATUS_ID_CHANGE:
      return `change status from ${comment.PreviousGroup || ''} to ${comment.Group || ''}.`;
    case COMMENT_TYPE.STATUS_CHANGE:
      return comment.Status ? 'completed task.' : 're-opened task.';
    case COMMENT_TYPE.UPDATE_TASK:
      return 'updated task.';
    case COMMENT_TYPE.UNASSIGN:
      return 'removed assignee.';
    case COMMENT_TYPE.REMOVE_ASSIGNEE:
      return `unassigned task to ${assigneeName}.`;
    case COMMENT_TYPE.ESTIMATE_TIME: {
      const hasEstimate =
        comment.EstimateTime !== 0 && comment.EstimateTime !== null && comment.EstimateTime !== undefined;
      return `set estimate time to ${hasEstimate ? floatToHHMM(comment.EstimateTime as number) : ''} hours.`;
    }
    case COMMENT_TYPE.CHANGE_GROUP:
      return `change group from ${comment.PreviousGroup || ''} to ${comment.Group || ''}.`;
    case COMMENT_TYPE.ADD_LABEL:
      return `Added label ${comment.Group || ''}.`;
    case COMMENT_TYPE.REMOVE_LABEL:
      return `Removed label ${comment.Group || ''}.`;
    case COMMENT_TYPE.REMOVE_DUE_DATE:
      return 'remove due date.';
    case COMMENT_TYPE.CHANGE_DUE_DATE:
      return `change due date from ${formatDueDate(comment.PreviousDueDate)} to ${formatDueDate(comment.DueDate)}.`;
    case COMMENT_TYPE.SET_DUE_DATE:
      return `set due date to ${formatDueDate(comment.DueDate)}.`;
    case COMMENT_TYPE.ADD_ASSIGNEE:
      return `assign task to ${assigneeName}.`;
    case COMMENT_TYPE.SET_MILESTONE:
      return `added task in milestone ${comment.Group || ''}.`;
    case COMMENT_TYPE.CREATE_TASK:
      return 'created the task.';
    case COMMENT_TYPE.CHANGE_PRIORITY:
      return `change priority from ${PRIORITY_ICON_NAMES[comment.PreviousPriority || 0]} to ${
        PRIORITY_ICON_NAMES[comment.Priority || 0]
      }.`;
    default:
      return null;
  }
};

// Builds the description column for a single activity. Returns null when the activity has no
// renderable text (kept consistent with the timeline, which hides such rows).
const getActivityDescription = (
  activity: TeamActivityInterface,
  users: TimelineUserLookup[] | undefined,
  workspaceName?: string
): string | null => {
  switch (activity.type) {
    case 'task_created':
      return 'created task';
    case 'project_created':
      return 'created project';
    case 'newProject_member_added':
      return `added ${findUserName(users, activity.memberUserId)} into project`;
    case 'invited_user':
      return `invited user ${activity.invitedUserName || 'Unknown'} into workspace ${workspaceName || ''}`.trim();
    case 'task_comment':
      return getCommentDescription(activity.comment || {}, users);
    default:
      return null;
  }
};

// Flattens the paginated team-timeline activities into CSV-ready rows. Activities with no
// textual description are dropped so the export mirrors what the user sees on screen.
export const getTimelineExportData = (
  activities: TeamActivityInterface[],
  users: TimelineUserLookup[] | undefined,
  workspaceName?: string
): TimelineExportRow[] => {
  return (activities || []).reduce<TimelineExportRow[]>((rows, activity) => {
    const description = getActivityDescription(activity, users, workspaceName);
    if (description === null) {
      return rows;
    }

    rows.push({
      date: activity.createdTime ? dayjs(activity.createdTime).format('MMM DD, YYYY hh:mm A') : '',
      member: activity.createdBy === 'Github' ? 'Github' : findUserName(users, activity.createdBy),
      activityType: ACTIVITY_TYPE_LABELS[activity.type] || activity.type,
      description,
      project: activity.projectName || '',
      task: activity.taskName || ''
    });
    return rows;
  }, []);
};
